import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/lib/supabase';

const menuDataFallback = {
  suppen: {
    title: 'Zuppe',
    subtitle: 'Suppen',
    items: [
      { name: 'Crema di Pomodoro', description: 'Feine Tomatencremesuppe', price: '7,50' },
      { name: 'Minestrone', description: 'Italienische Gemüsesuppe', price: '7,50' }
    ]
  },
  antipasti: {
    title: 'Antipasti',
    subtitle: 'Vorspeisen',
    items: [
      { name: 'Antipasto Misto', description: 'Gemischte Vorspeisenplatte', price: '12,50' },
      { name: 'Bruschetta Classica', description: 'Geröstete Weißbrotscheiben mit frischen gewürzten Tomaten (4 Stück)', price: '7,50' },
      { name: 'Bruschetta con Salmone alla Griglia', description: 'Geröstete Weißbrotscheiben mit gebratenem Lachs (4 Stück)', price: '9,50' },
      { name: 'Vitello Tonnato', description: 'Dünn geschnittenes Kalbfleisch mit feiner Thunfischsoße und Apfelkapern', price: '13,50' },
      { name: 'Mozzarella alla Caprese', description: 'Mozzarella mit Tomaten und frischem Basilikum', price: '10,50' },
      { name: 'Carpaccio di Manzo', description: 'Carpaccio vom Rinderfilet mit Rucola und Parmesan', price: '14,50' },
      { name: 'Carpaccio di Lachs', description: 'Carpaccio vom Lachs mit Zitronen-Vinaigrette', price: '15,50' }
    ]
  },
  salate: {
    title: 'Insalate',
    subtitle: 'Salate',
    items: [
      { name: 'Insalata di Pomodori e Cetrioli', description: 'Tomatensalat mit Gurken und Zwiebeln', price: '7,50' },
      { name: 'Insalata Mista', description: 'Gemischter Salat', price: '7,50' },
      { name: 'Insalata Verde', description: 'Grüner Salat', price: '5,50' },
      { name: 'Insalata Fagioli con Tonno', description: 'Bohnensalat mit Thunfisch', price: '9,50' },
      { name: 'Insalata Mista Caesar', description: 'Caesar-Salat mit Hähnchen, geröstetem Brot und Parmesansplitter', price: '13,50' },
      { name: 'Insalata della Casa', description: 'Großer gemischter Salat mit Thunfisch, Oliven, Ei und Mais', price: '12,50' },
      { name: 'Formaggio di Capra alla Griglia', description: 'Rucolasalat mit gebratenem Ziegenkäse, Kirschtomaten und Honig', price: '13,50' },
      { name: 'Insalata di Tacchino', description: 'Gemischter Salat mit gebratenen Truthahnstreifen', price: '12,50' },
      { name: 'Insalata Mista con Salmone', description: 'Gemischter Salat mit Lachs und Kräuter-Würzöl', price: '14,50' }
    ]
  },
  pasta: {
    title: 'Paste',
    subtitle: 'Nudeln',
    items: [
      { name: 'Spaghetti Aglio e Olio e Peperoncino', description: 'Spaghetti mit Knoblauch, Öl und Chili-Schoten', price: '8,50' },
      { name: 'Spaghetti Napoli', description: 'Spaghetti mit Basilikum in Tomatensoße', price: '10,50' },
      { name: 'Spaghetti alla Carbonara', description: 'Mit Speck, Eigelb und Sahnesoße', price: '12,50' },
      { name: 'Spaghetti Bolognese', description: 'Spaghetti mit Hackfleischsoße', price: '12,50' },
      { name: 'Spaghetti Amatriciana', description: 'Spaghetti mit Zwiebeln, Speck in Tomatensoße', price: '12,50' },
      { name: 'Spaghetti Puttanesca', description: 'Spaghetti mit Knoblauch, Kapern, Oliven und Tomatensoße', price: '12,50' },
      { name: 'Spaghetti ai Frutti di Mare', description: 'Spaghetti mit Meeresfrüchten', price: '14,50' },
      { name: "Penne all'Arrabbiata", description: 'Penne mit Knoblauch, Chili-Schoten und Petersilie in Tomatensoße', price: '10,50' },
      { name: 'Penne al Salmone e Broccoli', description: 'Penne mit Lachs und Brokkoli in Tomaten-Sahne-Soße', price: '13,50' },
      { name: 'Lasagna al Forno', description: 'Lasagna im Ofen überbacken', price: '12,50' },
      { name: 'Penne quattro Formaggi', description: 'Penne mit verschiedenen Käsesorten in Sahnesoße', price: '12,50' },
      { name: "Tagliatelle all'Emiliana", description: 'Tagliatelle mit frischen Pilzen, Erbsen, Vorderschinken in Sahnesoße', price: '12,50' },
      { name: 'Tagliatelle alla Vegetariana', description: 'Tagliatelle mit verschiedenen Gemüse', price: '12,50' },
      { name: 'Tortellini Panna e Prosciutto', description: 'Tortellini mit Vorderschinken in Sahnesoße', price: '12,50' },
      { name: 'Penne al Forno', description: 'Penne mit frischen Pilzen, Erbsen, Vorderschinken und Mozzarella im Ofen überbacken', price: '12,50' },
      { name: 'Gnocchi Sorrentina', description: 'Gnocchi mit Tomatensoße und Mozzarella überbacken', price: '13,50' }
    ]
  },
  pizza: {
    title: 'Pizze',
    subtitle: 'Pizza',
    items: [
      { name: 'Pizza Pane', description: 'Mit Knoblauch', price: '7,50' },
      { name: 'Pizza Pane Bianca', description: 'Pizzabrot mit Olivenöl und Rosmarin', price: '7,50' },
      { name: 'Pizza Margherita', description: 'Mit Tomatensoße und Mozzarella', price: '9,50' },
      { name: 'Pizza Funghi Freschi', description: 'Mit Tomatensoße, Mozzarella, frischen Pilzen', price: '11,50' },
      { name: 'Pizza Prosciutto', description: 'Mit Tomatensoße, Mozzarella und Vorderschinken', price: '11,50' },
      { name: 'Pizza Salame', description: 'Mit Tomatensoße, Mozzarella, Salami', price: '11,50' },
      { name: 'Pizza Regina', description: 'Mit Tomatensoße, Mozzarella, Vorderschinken, frischen Pilzen', price: '12,50' },
      { name: 'Pizza Romana', description: 'Mit Tomatensoße, Mozzarella, Salami und frischen Pilzen', price: '12,50' },
      { name: 'Pizza Hawaii', description: 'Mit Tomatensoße, Mozzarella, Vorderschinken, Ananas', price: '12,50' },
      { name: 'Pizza Diavola', description: 'Mit Tomatensoße, Mozzarella, Vorderschinken, scharfe Salami, Peperoni und Knoblauch', price: '12,50' },
      { name: 'Pizza Vegetariana', description: 'Mit Tomatensoße, Mozzarella und verschiedenem Gemüse', price: '12,50' },
      { name: 'Pizza Calzone', description: 'Mit Tomatensoße, Mozzarella, Vorderschinken, frischen Pilzen und Artischocken', price: '12,50' },
      { name: 'Pizza quattro Stagioni', description: 'Mit Tomatensoße, Mozzarella, Vorderschinken, frischen Pilzen, Artischocken und Oliven', price: '12,50' },
      { name: 'Pizza Capricciosa', description: 'Mit Tomatensoße, Mozzarella, Salami, frischen Pilzen, Peperoni und Oliven', price: '12,50' },
      { name: 'Pizza Gorgonzola e Spinaci', description: 'Mit Tomatensoße, Mozzarella, Gorgonzola und Blattspinat', price: '12,50' },
      { name: 'Pizza Agrodolce', description: 'Mit Mozzarella, Birne und Walnüsse', price: '12,50' },
      { name: 'Pizza Parma', description: 'Mit Tomatensoße, Mozzarella, Rucola, Parmaschinken und Parmesansplittern', price: '14,50' },
      { name: 'Pizza quattro Formaggi', description: 'Mit Tomatensoße, Mozzarella und vier verschiedenen Käsesorten', price: '13,50' },
      { name: 'Pizza della Casa', description: 'Pizza nach Art des Hauses', price: '13,50' },
      { name: 'Pizza Tonno e Cipolla', description: 'Mit Tomatensoße, Mozzarella, Thunfisch und Zwiebeln', price: '13,50' },
      { name: 'Pizza Frutti di Mare', description: 'Mit Tomatensoße, Meeresfrüchten', price: '14,50' },
      { name: 'Pizza Fresca', description: 'Mit Tomatensoße, Mozzarella, Rucola, und Parmesansplitter', price: '13,50' },
      { name: 'Pizza Calabrese', description: 'Mit Tomatensoße, Mozzarella, scharfer Salami und Steinpilzen', price: '13,50' },
      { name: 'Pizza Contadina', description: 'Mit Tomatensoße, Mozzarella, Paprika, Auberginen und Gorgonzola', price: '13,50' },
      { name: 'Pizza Melanzane', description: 'Mit Tomatensoße, Mozzarella, Auberginen und Schafkäse', price: '12,50' },
      { name: 'Pizza Salmone e Broccoli', description: 'Mit Lachs und Brokkoli', price: '14,50' }
    ]
  },
  fleisch_pfanne: {
    title: 'Carni in Padella',
    subtitle: 'Fleisch aus der Pfanne',
    items: [
      { name: 'Scaloppina ai Funghi', description: 'Kalbsmedaillons mit frischen Pilzen', price: '21,50' },
      { name: 'Saltimbocca alla Romana', description: 'Kalbsmedaillons mit Parmaschinken und Salbei', price: '21,50' },
      { name: 'Cotoletta alla Milanese di Tacchino', description: 'Paniertes Truthahnschnitzel', price: '19,50' },
      { name: 'Cotoletta alla Milanese di Vitello', description: 'Paniertes Kalbsschnitzel', price: '21,50' }
    ]
  },
  fleisch_grill: {
    title: 'Carni alla Griglia',
    subtitle: 'Fleisch vom Grill',
    items: [
      { name: 'Costata di Manzo', description: 'Argentinisches Rinderlendensteak vom Grill', price: '27,50' },
      { name: 'Tagliata di Manzo', description: 'Grillscheiben von der Rinderlende auf Rucola und Parmesan', price: '28,50' },
      { name: 'Petto di Pollo alla Griglia', description: 'Hähnchenbrust vom Grill mit Gemüse', price: '20,50' }
    ]
  },
  fisch: {
    title: 'Pesci alla Griglia',
    subtitle: 'Fisch vom Grill',
    items: [
      { name: 'Pesce Misto alla Griglia', description: 'Gemischte Fischplatte vom Grill', price: '27,50' },
      { name: 'Scampi alla Griglia', description: 'Salzwasser Scampi vom Grill', price: '28,50' },
      { name: 'Calamaretti alla Griglia', description: 'Baby Calamari vom Grill', price: '21,50' }
    ]
  },
  kaese: {
    title: 'Formaggi',
    subtitle: 'Käse',
    items: [
      { name: 'Formaggio Misto', description: 'Gemischte Käseplatte', price: '12,50' }
    ]
  },
  dolci: {
    title: 'Dolci',
    subtitle: 'Dessert',
    items: [
      { name: 'Tartufo Eis con Baileys', description: 'Schwarzes Schokoladen-Trüffeleis in Baileys getränkt', price: '7,50' },
      { name: 'Tiramisù', description: 'Das klassische italienische Dessert', price: '7,50' }
    ]
  }
};

const categories = ['suppen', 'antipasti', 'salate', 'pasta', 'pizza', 'fleisch_pfanne', 'fleisch_grill', 'fisch', 'kaese', 'dolci'];

function buildMenuFromRows(rows) {
  const result = {};
  rows.forEach((row) => {
    if (!result[row.category]) {
      result[row.category] = { title: row.category_title, subtitle: row.category_subtitle || '', items: [] };
    }
    result[row.category].items.push({ name: row.name, description: row.description || '', price: row.price });
  });
  return result;
}

export default function Menu() {
  const [activeCategory, setActiveCategory] = useState('antipasti');
  const [menuData, setMenuData] = useState(menuDataFallback);
  const [dynamicCategories, setDynamicCategories] = useState(categories);

  useEffect(() => {
    supabase
      .from('menu_items')
      .select('*')
      .eq('karte', 'speisekarte')
      .order('sort_order')
      .then(({ data }) => {
        if (data && data.length > 0) {
          setMenuData(buildMenuFromRows(data));
          const cats = [...new Set(data.map((r) => r.category))];
          setDynamicCategories(cats);
          if (!cats.includes(activeCategory)) setActiveCategory(cats[0]);
        }
      });
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="menu-page">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_trattoria-girasole/artifacts/2cfcv6y5_Restaurant-Hallbergmoos-Italiener-79.jpg')`
          }}
        />
        <div className="absolute inset-0 bg-olive-900/70" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-4"
        >
          <p className="font-accent text-gold text-2xl mb-4">La Nostra Cucina</p>
          <h1 className="font-heading text-5xl md:text-6xl text-cream">Speisekarte</h1>
          <p className="font-body text-cream/80 mt-4 text-lg">
            Von Pizza und Pasta bis zu original italienischen Delikatessen
          </p>
        </motion.div>
      </section>

      {/* Menu Content */}
      <section className="py-16 md:py-24 bg-cream">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {dynamicCategories.map((category) => (
              <button
                key={category}
                data-testid={`menu-tab-${category}`}
                onClick={() => setActiveCategory(category)}
                className={`px-3 py-2 md:px-5 md:py-3 font-body uppercase text-xs tracking-wider transition-all duration-300 ${
                  activeCategory === category
                    ? 'bg-olive-900 text-cream'
                    : 'bg-white text-olive-900 hover:bg-olive-100 border border-olive-900/20'
                }`}
              >
                {menuData[category].title}
              </button>
            ))}
          </div>

          {/* Menu Items */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="text-center mb-12">
                <h2 className="font-heading text-4xl text-olive-900 mb-2">
                  {menuData[activeCategory].title}
                </h2>
                <p className="font-body text-muted-foreground italic">
                  {menuData[activeCategory].subtitle}
                </p>
              </div>

              <div className="space-y-6">
                {menuData[activeCategory].items.map((item, index) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    data-testid={`menu-item-${index}`}
                    className="flex justify-between items-start gap-4 pb-5 border-b border-cream-dark"
                  >
                    <div className="flex-1">
                      <h3 className="font-heading text-xl text-olive-900 mb-1">
                        {item.name}
                      </h3>
                      <p className="font-body text-muted-foreground text-sm leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                    <div className="font-heading text-xl text-terracotta whitespace-nowrap">
                      € {String(item.price).replace(/,(\d)(?=\s|$|\/)/g, ',$10')}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>


        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-cream-dark">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-olive-900 p-8 text-cream">
            <h3 className="font-heading text-2xl text-gold mb-4 text-center">Hinweise</h3>
            <p className="font-body text-cream/80 leading-relaxed text-sm text-center mb-4">
              Extra Knoblauch oder Zwiebel: 0,50 € • Extra Wunsch ab 1,00 €
            </p>
            <p className="font-body text-cream/70 leading-relaxed text-sm text-center">
              Liebe Gäste, wir verwenden frische Produkte, diese enthalten natürlich auch Allergene. 
              Sollten Sie allergisch sein, beraten wir Sie gerne – sprechen Sie uns einfach an!
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
